import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ConfirmationModal from '../../../../shared/components/ui/ConfirmationModal';

// Define a type for the account data (matches InvestmentAccount in DashboardMain)
interface Account {
  id: string; // Changed from accountId
  accountName: string;
  logoUrl: string | null; // Allow null
  isHidden?: boolean;
  company: string;
  brokerageAccountId?: string | null;
  isSnapTradeLinked?: boolean;
  // createdAt field might be needed if used for display later
}

interface AccountListProps {
  accounts: Account[];
  onAddAccountClick: () => void;
  onEditAccountClick: (id: string) => void; // Changed from accountId
  onDeleteAccountClick: (id: string) => void; // Changed from accountId
  onToggleHiddenClick: (id: string) => void; // Changed from accountId
  onReorderAccounts: (accountIds: string[]) => void;
}

// Sortable account item component
interface SortableAccountItemProps {
  account: Account;
  onHideClick: (accountId: string, isCurrentlyHidden: boolean) => void;
  onEditClick: (accountId: string) => void;
  onDeleteClick: (accountId: string, accountName: string) => void;
}

const SortableAccountItem: React.FC<SortableAccountItemProps> = ({
  account,
  onHideClick,
  onEditClick,
  onDeleteClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: account.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isHidden = account.isHidden ?? false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
    >
      {/* Drag Handle - Three vertical dots */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing mr-3 text-gray-400 hover:text-gray-600 flex items-center"
        title="Drag to reorder"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </div>

      <div className="flex items-center space-x-3 flex-1">
        <div className="relative h-8 w-8 shrink-0">
          <img
            src={account.logoUrl || '/default-logo.png'}
            alt={`${account.accountName} logo`}
            className="h-8 w-8 rounded-full object-contain bg-white border border-gray-200"
          />
          {(account.isSnapTradeLinked || Boolean(account.brokerageAccountId)) && (
            <span
              className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white shadow-sm"
              title="Linked external account"
              aria-label="Linked external account"
            >
              <span
                className="block h-3 w-3 bg-green-600"
                style={{
                  WebkitMask: "url('/icon-link-lrg.png') center / contain no-repeat",
                  mask: "url('/icon-link-lrg.png') center / contain no-repeat",
                }}
                aria-hidden="true"
              />
            </span>
          )}
        </div>
        <span className={`font-medium ${isHidden ? 'text-gray-400' : 'text-gray-800'}`}>
          {account.accountName}
        </span>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => onHideClick(account.id, isHidden)}
          title={isHidden ? 'Unhide Account' : 'Hide Account'}
          className={`p-1 rounded-full ${
            isHidden
              ? 'text-gray-600 bg-gray-200'
              : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'
          } focus:outline-none`}
        >
          <img
            src={isHidden ? '/icon-unhide-lrg.png' : '/icon-hide-lrg.png'}
            alt={isHidden ? 'Unhide Account' : 'Hide Account'}
            className="h-5 w-5"
          />
        </button>
        <button
          onClick={() => onEditClick(account.id)}
          title="Edit Account"
          className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-200 focus:outline-none"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
            <path
              fillRule="evenodd"
              d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <button
          onClick={() => onDeleteClick(account.id, account.accountName)}
          title="Delete Account"
          className="p-1 rounded-full text-red-400 hover:text-red-700 hover:bg-red-100 focus:outline-none"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

const AccountList: React.FC<AccountListProps> = ({
  accounts,
  onAddAccountClick,
  onEditAccountClick,
  onDeleteAccountClick,
  onToggleHiddenClick,
  onReorderAccounts,
}) => {
  const [localAccounts, setLocalAccounts] = useState(accounts);

  // Update local accounts when prop changes
  React.useEffect(() => {
    setLocalAccounts(accounts);
  }, [accounts]);

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalAccounts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Call the parent handler with new order
        onReorderAccounts(newOrder.map((account) => account.id));
        
        return newOrder;
      });
    }
  };
  // Hide/unhide account confirmation modal state
  const [isAccountHideConfirmationOpen, setIsAccountHideConfirmationOpen] = useState(false);
  const [isAccountUnhideConfirmationOpen, setIsAccountUnhideConfirmationOpen] = useState(false);
  const [accountToToggle, setAccountToToggle] = useState<{ id: string; isHidden: boolean } | null>(null);
  const [isHidingAccount, setIsHidingAccount] = useState(false);

  // Delete account confirmation modal state
  const [isDeleteAccountConfirmationOpen, setIsDeleteAccountConfirmationOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Handle account hide button click
  const handleHideAccountClick = (accountId: string, isCurrentlyHidden: boolean) => {
    setAccountToToggle({ id: accountId, isHidden: isCurrentlyHidden });
    if (isCurrentlyHidden) {
      setIsAccountUnhideConfirmationOpen(true);
    } else {
      setIsAccountHideConfirmationOpen(true);
    }
  };

  // Handle hide confirmation modal confirm
  const handleConfirmHideAccount = async () => {
    if (!accountToToggle || isHidingAccount) return;
    setIsHidingAccount(true);
    try {
      await onToggleHiddenClick(accountToToggle.id);
      setAccountToToggle(null);
      setIsAccountHideConfirmationOpen(false);
    } finally {
      setIsHidingAccount(false);
    }
  };

  // Handle hide confirmation modal cancel
  const handleCancelHideAccount = () => {
    if (isHidingAccount) return; // Prevent closing during loading
    setAccountToToggle(null);
    setIsAccountHideConfirmationOpen(false);
  };

  // Handle unhide confirmation modal confirm
  const handleConfirmUnhideAccount = async () => {
    if (!accountToToggle || isHidingAccount) return;
    setIsHidingAccount(true);
    try {
      await onToggleHiddenClick(accountToToggle.id);
      setAccountToToggle(null);
      setIsAccountUnhideConfirmationOpen(false);
    } finally {
      setIsHidingAccount(false);
    }
  };

  // Handle unhide confirmation modal cancel
  const handleCancelUnhideAccount = () => {
    if (isHidingAccount) return; // Prevent closing during loading
    setAccountToToggle(null);
    setIsAccountUnhideConfirmationOpen(false);
  };

  // Handle delete button click
  const handleDeleteAccountClick = (accountId: string, accountName: string) => {
    setAccountToDelete({ id: accountId, name: accountName });
    setIsDeleteAccountConfirmationOpen(true);
  };

  // Handle delete confirmation
  const handleConfirmDeleteAccount = async () => {
    if (!accountToDelete || isDeletingAccount) return;
    setIsDeletingAccount(true);
    try {
      await onDeleteAccountClick(accountToDelete.id);
      setAccountToDelete(null);
      setIsDeleteAccountConfirmationOpen(false);
    } catch (error) {
      // Keep modal open on error (toast already shown by hook)
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Handle delete cancel
  const handleCancelDeleteAccount = () => {
    if (isDeletingAccount) return;
    setAccountToDelete(null);
    setIsDeleteAccountConfirmationOpen(false);
  };

  return (
    <div className="space-y-4">
      <button
        onClick={onAddAccountClick}
        className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-dashed border-green-600 text-green-700 rounded-lg hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
            clipRule="evenodd"
          />
        </svg>
        <span>Add accounts</span>
      </button>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={localAccounts.map((a) => a.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {localAccounts.map((account) => (
              <SortableAccountItem
                key={account.id}
                account={account}
                onHideClick={handleHideAccountClick}
                onEditClick={onEditAccountClick}
                onDeleteClick={handleDeleteAccountClick}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Hide Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={isAccountHideConfirmationOpen}
        onClose={handleCancelHideAccount}
        title="Hide Account"
        message={<>Are you sure you want to hide this entire account from all your friends?<br />(Note: Only Gold & Silver tier friends are affected)</>}
        confirmLabel="Yes, hide account"
        cancelLabel="Cancel"
        onConfirm={handleConfirmHideAccount}
        onCancel={handleCancelHideAccount}
        isLoading={isHidingAccount}
      />

      {/* Unhide Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={isAccountUnhideConfirmationOpen}
        onClose={handleCancelUnhideAccount}
        title="Unhide Account"
        message={<>Are you sure you want to unhide this entire account from all your friends?<br />(Note: Only Gold & Silver tier friends are affected)</>}
        confirmLabel="Yes, unhide account"
        cancelLabel="Cancel"
        onConfirm={handleConfirmUnhideAccount}
        onCancel={handleCancelUnhideAccount}
        isLoading={isHidingAccount}
      />

      {/* Delete Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteAccountConfirmationOpen}
        onClose={handleCancelDeleteAccount}
        title="Delete Account"
        message={
          accountToDelete
            ? <>Are you sure you want to permanently delete the account <strong>"{accountToDelete.name}"</strong> and all its transactions? This action cannot be undone.</>
            : 'Are you sure you want to permanently delete this account and all its transactions? This action cannot be undone.'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteAccount}
        onCancel={handleCancelDeleteAccount}
        isLoading={isDeletingAccount}
      />
    </div>
  );
};

export default AccountList; 
